import { useEffect, useMemo, useState } from 'react';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useBoxes } from '@/lib/api/boxes';
import { useAlertBoxes, useAlertConfig } from '@/lib/api/monitoring';
import { useAlertEvents } from '@/lib/api/alertEvents';
import { useContacts } from '@/lib/api/contacts';
import { useSmsLog } from '@/lib/api/smsLog';
import { AlertingTab } from './alertingTab';
import { BoxesTab } from './boxesTab';
import { RecipientsTab } from './recipientsTab';
import { ScheduleTab } from './scheduleTab';

export function Monitoring() {
  const [config] = useAlertConfig();
  const [alertBoxes] = useAlertBoxes();
  const [boxes] = useBoxes();
  const [contacts] = useContacts();
  const [smsLog] = useSmsLog();
  const [events] = useAlertEvents();

  // Ticks every 30 s so the window status and snooze countdowns stay honest
  // without a page reload.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const enabled = !!config?.enabled;
  const recipients: string[] = useMemo(
    () => config?.recipients ?? [],
    [config]
  );

  // number -> contact name, for friendly display of recipients and log entries.
  const contactByNumber = useMemo(() => {
    const map: Record<string, string> = {};
    contacts?.forEach((c) => {
      if (c.number) map[c.number] = c.name;
    });
    return map;
  }, [contacts]);

  const label = (number: string) =>
    contactByNumber[number] ? `${contactByNumber[number]} (${number})` : number;

  return (
    <div className="w-full pb-4">
      <h1 className="text-3xl font-bold">Box Monitoring &amp; Alerts</h1>
      <Separator className="my-6" />

      <Tabs defaultValue="alerting">
        <TabsList>
          <TabsTrigger value="alerting">Alerting</TabsTrigger>
          <TabsTrigger value="boxes">Monitored Boxes</TabsTrigger>
          <TabsTrigger value="recipients">Recipients &amp; Contacts</TabsTrigger>
          <TabsTrigger value="schedule">Event Schedule</TabsTrigger>
        </TabsList>

        <TabsContent value="alerting">
          <AlertingTab
            enabled={enabled}
            events={events}
            now={now}
            smsLog={smsLog}
            label={label}
          />
        </TabsContent>

        <TabsContent value="boxes">
          <BoxesTab alertBoxes={alertBoxes} boxes={boxes} now={now} />
        </TabsContent>

        <TabsContent value="recipients">
          <RecipientsTab
            recipients={recipients}
            contacts={contacts}
            label={label}
          />
        </TabsContent>

        <TabsContent value="schedule">
          <ScheduleTab events={events} now={now} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default Monitoring;
